import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';

import useNotification from '../../../core/hooks/use_notification';
import { getTopProductsAction, getTopProductsState } from '../../../features/stats/get_top_products';
import { getSalesStatsNotificationData } from '../../../core/utils/notification_utils';
import { formatCompactCurrency, formatCompactNumber, formatCurrency, formatNumber, truncateLabel, useChartColors } from './sales_analysis_utils';
import ChartCard from './chart_card';

const LIMIT = 10;
const ROW_HEIGHT = 34;

const MetricEnum = {
    REVENUE: { value: 'REVENUE', label: 'Obrót', dataKey: 'revenue', format: formatCurrency },
    QUANTITY: { value: 'QUANTITY', label: 'Ilość', dataKey: 'quantity', format: value => `${ formatNumber(value) } szt.` }
};

const SortEnum = {
    DESC: { value: 'DESC', label: 'Najlepsze' },
    ASC: { value: 'ASC', label: 'Najsłabsze' }
};

const TopProductsChart = ({ query }) => {
    const chartColors = useChartColors();
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const getTopProducts = useSelector(getTopProductsState);

    const [metric, setMetric] = useState(MetricEnum.REVENUE.value);
    const [sort, setSort] = useState(SortEnum.DESC.value);

    useEffect(() => {
        dispatch(getTopProductsAction({ ...query, metric, sort, limit: LIMIT }))
            .then(response => showErrorMessage(getSalesStatsNotificationData(response)));
    }, [dispatch, query, metric, sort, showErrorMessage]);

    const selectedMetric = MetricEnum[metric];
    const dataset = (getTopProducts?.data || []).map(row => ({
        label: row?.productName,
        type: row?.type,
        revenue: Number(row?.revenue),
        quantity: row?.quantity
    }));

    return (
        <ChartCard
            title={ sort === SortEnum.DESC.value ? 'Najlepiej sprzedające się produkty' : 'Najsłabiej sprzedające się produkty' }
            subtitle={ sort === SortEnum.ASC.value ? 'Spośród produktów, które sprzedały się co najmniej raz' : `Top ${ LIMIT }` }
            loading={ getTopProducts?.loading }
            empty={ dataset.length === 0 }
            height={ Math.max(120, dataset.length * ROW_HEIGHT + 48) }
            actions={
                <Stack direction="row" gap={ 1 } flexWrap="wrap">
                    <ToggleButtonGroup size="small" exclusive value={ sort } onChange={ (_, value) => value && setSort(value) }>
                        { Object.values(SortEnum).map(item => <ToggleButton key={ item.value } value={ item.value }>{ item.label }</ToggleButton>) }
                    </ToggleButtonGroup>
                    <ToggleButtonGroup size="small" exclusive value={ metric } onChange={ (_, value) => value && setMetric(value) }>
                        { Object.values(MetricEnum).map(item => <ToggleButton key={ item.value } value={ item.value }>{ item.label }</ToggleButton>) }
                    </ToggleButtonGroup>
                </Stack>
            }
        >
            {
                dataset.length > 0 && (
                    <BarChart
                        layout="horizontal"
                        dataset={ dataset }
                        series={ [{
                            dataKey: selectedMetric.dataKey,
                            label: selectedMetric.label,
                            color: chartColors.primary,
                            valueFormatter: (value, { dataIndex }) => `${ selectedMetric.format(value) } · ${ dataset[dataIndex]?.type }`
                        }] }
                        yAxis={ [{ scaleType: 'band', dataKey: 'label', valueFormatter: (label, context) => context.location === 'tick' ? truncateLabel(label) : label }] }
                        xAxis={ [{ valueFormatter: value => metric === MetricEnum.REVENUE.value ? formatCompactCurrency(value) : formatCompactNumber(value), tickNumber: 5 }] }
                        grid={ { vertical: true } }
                        borderRadius={ 4 }
                        margin={ { left: 220, right: 40, top: 8, bottom: 32 } }
                        slotProps={ { legend: { hidden: true } } }
                    />
                )
            }
        </ChartCard>
    );
};

export default TopProductsChart;
